import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Tenant } from '@/contexts/TenantContext';
import type { LocationWorkingHours } from '@/types';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, MapPin, FileText, User, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';

interface OnboardingWizardProps {
  tenant: Tenant;
  profile: { id: string; full_name: string | null; role: string };
  onComplete: () => void;
}

const STEPS = ['business', 'location', 'billing', 'profile'] as const;
type Step = typeof STEPS[number];

const STEP_META = [
  { key: 'business' as const, label: 'Επιχείρηση', icon: Building2 },
  { key: 'location' as const, label: 'Τοποθεσία', icon: MapPin },
  { key: 'billing' as const, label: 'Τιμολόγηση', icon: FileText },
  { key: 'profile' as const, label: 'Προφίλ', icon: User },
];

const DAY_LABELS: Record<string, string> = {
  monday: 'Δευτέρα',
  tuesday: 'Τρίτη',
  wednesday: 'Τετάρτη',
  thursday: 'Πέμπτη',
  friday: 'Παρασκευή',
  saturday: 'Σάββατο',
  sunday: 'Κυριακή',
};

const DEFAULT_WORKING_HOURS: LocationWorkingHours = {
  monday: { enabled: true, start: '09:00', end: '21:00', break_start: null, break_end: null },
  tuesday: { enabled: true, start: '09:00', end: '21:00', break_start: null, break_end: null },
  wednesday: { enabled: true, start: '09:00', end: '21:00', break_start: null, break_end: null },
  thursday: { enabled: true, start: '09:00', end: '21:00', break_start: null, break_end: null },
  friday: { enabled: true, start: '09:00', end: '21:00', break_start: null, break_end: null },
  saturday: { enabled: true, start: '09:00', end: '17:00', break_start: null, break_end: null },
  sunday: { enabled: false, start: '09:00', end: '17:00', break_start: null, break_end: null },
};

interface OnboardingState {
  // Step 1: Business
  businessName: string;
  businessPhone: string;
  businessEmail: string;
  // Step 2: Location
  locationName: string;
  locationAddress: string;
  locationCity: string;
  locationPostalCode: string;
  skipWorkingHours: boolean;
  workingHours: LocationWorkingHours;
  // Step 3: Billing
  billingCompanyName: string;
  billingAddress: string;
  billingCity: string;
  billingPostalCode: string;
  billingTaxId: string;
  billingVatNumber: string;
  // Step 4: Profile
  fullName: string;
}

export function OnboardingWizard({ tenant, profile, onComplete }: OnboardingWizardProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<OnboardingState>({
    businessName: tenant.name || '',
    businessPhone: tenant.phone || '',
    businessEmail: tenant.email || '',
    locationName: tenant.name || '',
    locationAddress: '',
    locationCity: '',
    locationPostalCode: '',
    skipWorkingHours: false,
    workingHours: { ...DEFAULT_WORKING_HOURS },
    billingCompanyName: tenant.billing_company_name || '',
    billingAddress: tenant.billing_address || '',
    billingCity: tenant.billing_city || '',
    billingPostalCode: tenant.billing_postal_code || '',
    billingTaxId: tenant.billing_tax_id || '',
    billingVatNumber: tenant.billing_vat_number || '',
    fullName: profile.full_name || '',
  });

  const currentStep = STEPS[stepIndex];

  const updateState = (partial: Partial<OnboardingState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  };

  const updateWorkingHours = (day: string, changes: Partial<LocationWorkingHours[keyof LocationWorkingHours]>) => {
    updateState({
      workingHours: {
        ...state.workingHours,
        [day]: { ...state.workingHours[day as keyof LocationWorkingHours], ...changes },
      },
    });
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'business':
        return state.businessName.trim().length >= 2;
      case 'location':
        return state.locationName.trim().length >= 2;
      case 'billing':
        return true; // all optional
      case 'profile':
        return state.fullName.trim().length >= 2;
      default:
        return false;
    }
  };

  const saveStep = async () => {
    setSaving(true);
    try {
      switch (currentStep) {
        case 'business': {
          const { error } = await supabase
            .from('tenants')
            .update({
              name: state.businessName.trim(),
              phone: state.businessPhone.trim() || null,
              email: state.businessEmail.trim() || null,
            })
            .eq('id', tenant.id);
          if (error) throw error;
          break;
        }
        case 'location': {
          // Check if a primary location already exists
          const { data: existing } = await supabase
            .from('locations')
            .select('id')
            .eq('tenant_id', tenant.id)
            .eq('is_primary', true)
            .maybeSingle();

          const locationData = {
            tenant_id: tenant.id,
            name: state.locationName.trim(),
            address: state.locationAddress.trim() || null,
            city: state.locationCity.trim() || null,
            postal_code: state.locationPostalCode.trim() || null,
            is_active: true,
            is_primary: true,
            working_hours: state.skipWorkingHours ? DEFAULT_WORKING_HOURS : state.workingHours,
          };

          if (existing) {
            const { error } = await supabase
              .from('locations')
              .update(locationData)
              .eq('id', existing.id);
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('locations')
              .insert(locationData);
            if (error) throw error;
          }
          break;
        }
        case 'billing': {
          const { error } = await supabase
            .from('tenants')
            .update({
              billing_company_name: state.billingCompanyName.trim() || null,
              billing_address: state.billingAddress.trim() || null,
              billing_city: state.billingCity.trim() || null,
              billing_postal_code: state.billingPostalCode.trim() || null,
              billing_tax_id: state.billingTaxId.trim() || null,
              billing_vat_number: state.billingVatNumber.trim() || null,
            })
            .eq('id', tenant.id);
          if (error) throw error;
          break;
        }
        case 'profile': {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({ full_name: state.fullName.trim() })
            .eq('id', profile.id);
          if (profileError) throw profileError;

          const { error: tenantError } = await supabase
            .from('tenants')
            .update({ is_onboarded: true })
            .eq('id', tenant.id);
          if (tenantError) throw tenantError;

          onComplete();
          return;
        }
      }
      setStepIndex((i) => i + 1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Σφάλμα αποθήκευσης';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const accentColor = tenant.primary_color || '#6366f1';

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6 [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">Ρύθμιση Επιχείρησης</DialogTitle>
          <DialogDescription>
            Συμπληρώστε τα βασικά στοιχεία για να ξεκινήσετε
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 sm:gap-2 mb-4 sm:mb-6">
          {STEP_META.map((meta, i) => {
            const Icon = meta.icon;
            const isActive = i === stepIndex;
            const isDone = i < stepIndex;
            const highlighted = isActive || isDone;
            return (
              <div key={meta.key} className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
                <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors shrink-0 ${highlighted ? '' : 'bg-muted text-muted-foreground'}`}
                    style={highlighted ? { backgroundColor: accentColor, color: '#fff' } : undefined}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`hidden sm:block text-xs font-medium truncate max-w-full ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {meta.label}
                  </span>
                </div>
                {i < STEP_META.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 rounded-full sm:-mt-5 ${isDone ? '' : 'bg-muted'}`}
                    style={isDone ? { backgroundColor: accentColor } : undefined}
                  />
                )}
              </div>
            );
          })}
        </div>
        {/* Active step label (mobile only) */}
        <div className="sm:hidden text-center -mt-2 mb-2 text-sm font-medium text-foreground">
          {STEP_META[stepIndex]?.label}
        </div>

        {/* Step content */}
        <div className="space-y-4 min-h-[200px]">
          {currentStep === 'business' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="ob-name">Όνομα Επιχείρησης *</Label>
                <Input
                  id="ob-name"
                  value={state.businessName}
                  onChange={(e) => updateState({ businessName: e.target.value })}
                  placeholder="π.χ. Beauty Salon Αθήνα"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ob-phone">Τηλέφωνο</Label>
                <Input
                  id="ob-phone"
                  type="tel"
                  value={state.businessPhone}
                  onChange={(e) => updateState({ businessPhone: e.target.value })}
                  placeholder="π.χ. 210 1234567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ob-email">Email</Label>
                <Input
                  id="ob-email"
                  type="email"
                  value={state.businessEmail}
                  onChange={(e) => updateState({ businessEmail: e.target.value })}
                  placeholder="π.χ. info@example.com"
                />
              </div>
            </>
          )}

          {currentStep === 'location' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="ob-loc-name">Όνομα Τοποθεσίας *</Label>
                <Input
                  id="ob-loc-name"
                  value={state.locationName}
                  onChange={(e) => updateState({ locationName: e.target.value })}
                  placeholder="π.χ. Κεντρικό Κατάστημα"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ob-loc-address">Διεύθυνση</Label>
                  <Input
                    id="ob-loc-address"
                    value={state.locationAddress}
                    onChange={(e) => updateState({ locationAddress: e.target.value })}
                    placeholder="π.χ. Ερμού 10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-loc-city">Πόλη</Label>
                  <Input
                    id="ob-loc-city"
                    value={state.locationCity}
                    onChange={(e) => updateState({ locationCity: e.target.value })}
                    placeholder="π.χ. Αθήνα"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ob-loc-postal">Τ.Κ.</Label>
                <Input
                  id="ob-loc-postal"
                  value={state.locationPostalCode}
                  onChange={(e) => updateState({ locationPostalCode: e.target.value })}
                  placeholder="π.χ. 10563"
                  className="w-32"
                />
              </div>

              {/* Working Hours */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label>Ωράριο Λειτουργίας</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="ob-skip-hours"
                      checked={state.skipWorkingHours}
                      onCheckedChange={(checked) => updateState({ skipWorkingHours: !!checked })}
                    />
                    <Label htmlFor="ob-skip-hours" className="text-sm text-muted-foreground cursor-pointer">
                      Παράλειψη (ρύθμιση αργότερα)
                    </Label>
                  </div>
                </div>
                {!state.skipWorkingHours && (
                  <div className="space-y-2">
                    {Object.entries(DAY_LABELS).map(([day, label]) => {
                      const hours = state.workingHours[day as keyof LocationWorkingHours];
                      return (
                        <div key={day} className="text-sm border-b border-border/40 pb-2 last:border-0 last:pb-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Switch
                              checked={hours.enabled}
                              onCheckedChange={(checked) => updateWorkingHours(day, { enabled: checked })}
                            />
                            <span className={`w-20 sm:w-24 font-medium ${hours.enabled ? '' : 'text-muted-foreground'}`}>
                              {label}
                            </span>
                            {hours.enabled && (
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Input
                                  type="time"
                                  value={hours.start}
                                  onChange={(e) => updateWorkingHours(day, { start: e.target.value })}
                                  className="flex-1 min-w-0 sm:w-[110px] sm:flex-none text-sm"
                                />
                                <span className="text-muted-foreground">-</span>
                                <Input
                                  type="time"
                                  value={hours.end}
                                  onChange={(e) => updateWorkingHours(day, { end: e.target.value })}
                                  className="flex-1 min-w-0 sm:w-[110px] sm:flex-none text-sm"
                                />
                              </div>
                            )}
                          </div>
                          {hours.enabled && (
                            <div className="flex flex-wrap items-center gap-2 mt-2 sm:ml-[calc(2.25rem+0.5rem)]">
                              <span className="text-xs text-muted-foreground w-20 sm:w-24">Διάλειμμα:</span>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Input
                                  type="time"
                                  value={hours.break_start || ''}
                                  onChange={(e) => updateWorkingHours(day, { break_start: e.target.value || null })}
                                  className="flex-1 min-w-0 sm:w-[110px] sm:flex-none text-sm"
                                  placeholder="--:--"
                                />
                                <span className="text-muted-foreground">-</span>
                                <Input
                                  type="time"
                                  value={hours.break_end || ''}
                                  onChange={(e) => updateWorkingHours(day, { break_end: e.target.value || null })}
                                  className="flex-1 min-w-0 sm:w-[110px] sm:flex-none text-sm"
                                  placeholder="--:--"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {currentStep === 'billing' && (
            <>
              <p className="text-sm text-muted-foreground">
                Προαιρετικά στοιχεία τιμολόγησης. Μπορείτε να τα συμπληρώσετε αργότερα από τις Ρυθμίσεις.
              </p>
              <div className="space-y-2">
                <Label htmlFor="ob-bill-company">Επωνυμία</Label>
                <Input
                  id="ob-bill-company"
                  value={state.billingCompanyName}
                  onChange={(e) => updateState({ billingCompanyName: e.target.value })}
                  placeholder="π.χ. BEAUTY SALON Ε.Π.Ε."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ob-bill-address">Διεύθυνση</Label>
                  <Input
                    id="ob-bill-address"
                    value={state.billingAddress}
                    onChange={(e) => updateState({ billingAddress: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-bill-city">Πόλη</Label>
                  <Input
                    id="ob-bill-city"
                    value={state.billingCity}
                    onChange={(e) => updateState({ billingCity: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ob-bill-postal">Τ.Κ.</Label>
                  <Input
                    id="ob-bill-postal"
                    value={state.billingPostalCode}
                    onChange={(e) => updateState({ billingPostalCode: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-bill-tax">ΑΦΜ</Label>
                  <Input
                    id="ob-bill-tax"
                    value={state.billingTaxId}
                    onChange={(e) => updateState({ billingTaxId: e.target.value })}
                    placeholder="π.χ. 123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-bill-vat">ΔΟΥ</Label>
                  <Input
                    id="ob-bill-vat"
                    value={state.billingVatNumber}
                    onChange={(e) => updateState({ billingVatNumber: e.target.value })}
                    placeholder="π.χ. Α' Αθηνών"
                  />
                </div>
              </div>
            </>
          )}

          {currentStep === 'profile' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="ob-fullname">Ονοματεπώνυμο *</Label>
                <Input
                  id="ob-fullname"
                  value={state.fullName}
                  onChange={(e) => updateState({ fullName: e.target.value })}
                  placeholder="π.χ. Γιώργος Παπαδόπουλος"
                />
                <p className="text-xs text-muted-foreground">
                  Το όνομά σας όπως θα εμφανίζεται στην εφαρμογή
                </p>
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-2 pt-4 border-t flex-wrap">
          <Button
            variant="outline"
            onClick={() => setStepIndex((i) => i - 1)}
            disabled={stepIndex === 0 || saving}
            className="px-3 sm:px-4"
          >
            <ChevronLeft className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Πίσω</span>
          </Button>
          <span className="text-sm text-muted-foreground">
            {stepIndex + 1} / {STEPS.length}
          </span>
          <Button
            onClick={saveStep}
            disabled={!canProceed() || saving}
            style={{ backgroundColor: accentColor }}
          >
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {stepIndex === STEPS.length - 1 ? 'Ολοκλήρωση' : 'Επόμενο'}
            {stepIndex < STEPS.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
